import React, { Component, MouseEvent, ReactElement } from 'react';
import PropTypes from 'prop-types';
import 'emoji-mart/css/emoji-mart.css';
import { BaseEmoji, CustomEmoji, Emoji, EmojiData } from 'emoji-mart';
import { css } from '@emotion/react';
import { EmojiPluginStore } from '../../index';
import addEmoji, { Mode as AddEmojiMode } from '../../modifiers/addEmoji';
import { logger } from '$/utils/logger';
import EmojiPicker from './EmojiPicker';
import tw from 'twin.macro';

export type EmojiSelectPubParams = Record<string, unknown>;

interface EmojiSelectParams extends EmojiSelectPubParams {
  store: EmojiPluginStore;
  isRtl?: boolean;
  selectButtonContent?: string;
  emojiSuggestionsEnabled?: boolean;
}

export default class EmojiSelect extends Component<EmojiSelectParams> {
  static propTypes = {
    store: PropTypes.object.isRequired,
    isRtl: PropTypes.bool,
    selectButtonContent: PropTypes.string,
  };

  // Start the selector closed
  state = {
    isOpen: false,
  };

  // When the selector is open and users click anywhere on the page,
  // the selector should close
  componentDidMount(): void {
    document.addEventListener('click', this.closePopover);
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this.closePopover);
  }

  onClick = (event: MouseEvent): void => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };

  onButtonMouseUp = (): void => (this.state.isOpen ? this.closePopover() : this.openPopover());

  // Open the popover
  openPopover = (): void => {
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
      });
    }
  };

  // Close the popover
  closePopover = (): void => {
    if (this.state.isOpen) {
      this.setState({
        isOpen: false,
      });
    }
  };

  onEmojiSelect = (emojiSymbol: EmojiData) => {
    if (!this.props.store.getEditorState || !this.props.store.setEditorState) {
      logger.error(
        'EditableInput',
        'Emoji -> onEmojiSelect: select emoji fired on unbinded instace.'
      );
      return;
    }

    let emoji: string = '';
    const baseEmojiSymbol = emojiSymbol as BaseEmoji;
    const customEmojiSymbol = emojiSymbol as CustomEmoji;

    if (baseEmojiSymbol.native) {
      emoji = baseEmojiSymbol.native;
    }

    if (!emoji && customEmojiSymbol.short_names && customEmojiSymbol.short_names.length > 0) {
      // Custom emoji case
      emoji = `:${customEmojiSymbol.short_names[0]}:`;
    }

    const newEditorState = addEmoji(this.props.store.getEditorState(), emoji, AddEmojiMode.INSERT);

    this.props.store.setEditorState(newEditorState);
  };

  render(): ReactElement {
    const { selectButtonContent, isRtl, emojiSuggestionsEnabled } = this.props;

    return (
      <div
        css={css`
          position: absolute;
          right: ${isRtl ? 'auto' : '4rem'};
          left: ${isRtl ? '4rem' : 'auto'};
          bottom: 1.25rem;
          border: 0;
          z-index: 1;
          display: inline-flex;
          .emoji-button {
            display: inline-flex;
            cursor: pointer;
            .emoji-mart-emoji {
              height: 24px;
            }
          }
          ul {
            margin: 0 !important;
            padding: 0 !important;
            display: block !important;
            li {
              button {
                &.emoji-mart-emoji {
                  span {
                    cursor: pointer;
                  }
                }
              }
            }
          }
          input {
            border: 1px solid #d9d9d9 !important;
            &:focus {
              border-color: #d9d9d9 !important;
            }
          }
          .emoji-mart {
            .emoji-mart-bar {
              .emoji-mart-preview-data {
                text-align: left;
                .emoji-mart-title-label {
                  font-size: 20px;
                }
                .emoji-mart-preview-emoticons {
                  display: none;
                }
                .emoji-mart-preview-shortnames {
                  display: ${emojiSuggestionsEnabled ? 'block' : 'none'};
                }
              }
              .emoji-mart-anchor-selected {
                ${tw`text-color-brand!`}
                .emoji-mart-anchor-bar {
                  ${tw`bg-color-brand!`}
                }
              }
            }
          }
          @media screen and (max-width: 769px) {
            display: none;
          }
        `}
        onClick={this.onClick}
      >
        <button className='emoji-button' onMouseUp={this.onButtonMouseUp} type='button'>
          <Emoji emoji={selectButtonContent || `:slightly_smiling_face:`} size={24} />
        </button>
        {this.state.isOpen && (
          <EmojiPicker isRtl={Boolean(isRtl)} onEmojiSelect={this.onEmojiSelect} />
        )}
      </div>
    );
  }
}
